import { useState } from 'react'
import { X, ChevronRight, ChevronLeft, Building2 } from 'lucide-react'
import { COUNTRIES, INDUSTRIES } from '../lib/localization'
import { getCountryIdTypes } from '../lib/idTypes'

const EMPTY = {
  fullName: '',
  idType: '',
  phone: '',
  address: '',
  businessName: '',
  country: 'Nigeria',
  corporateAddress: '',
  industry: INDUSTRIES[0],
}

export default function OnboardingWizard({ open, onClose, onSave, onSkip }) {
  const [step, setStep] = useState(1)
  const [data, setData] = useState(EMPTY)

  if (!open) return null

  const set = (k, v) => setData((p) => ({ ...p, [k]: v }))

  const step1Valid = data.fullName && data.idType && data.phone && data.address
  const step2Valid = data.businessName && data.country && data.corporateAddress && data.industry

  const next = () => step1Valid && setStep(2)
  const back = () => setStep(1)

  const finish = () => {
    if (step2Valid) {
      onSave?.(data)
      setData(EMPTY)
      setStep(1)
      onClose?.()
    }
  }

  const handleSkip = () => {
    setData(EMPTY)
    setStep(1)
    onSkip?.()
    onClose?.()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} aria-hidden />
      
      {/* ✅ Fixed: Proper responsive sizing */}
      <div className="relative bg-surface border border-neutral-200 w-full max-w-sm sm:max-w-md md:max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in">
        
        {/* Header - Sticky with Skip and Close */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-neutral-200 sticky top-0 bg-surface z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand flex items-center justify-center rounded-lg shrink-0">
              <Building2 size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-ink">Complete your onboarding</h2>
              <p className="text-[11px] text-muted">Step {step} of 2</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleSkip} 
              className="text-xs text-muted hover:text-brand transition-colors hidden sm:inline-block"
            >
              Skip
            </button>
            <button 
              onClick={onClose} 
              className="text-neutral-400 hover:text-ink p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 sm:px-6 py-5">
          {step === 1 ? (
            <Step1 data={data} set={set} />
          ) : (
            <Step2 data={data} set={set} onBack={() => setStep(1)} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-neutral-200 sticky bottom-0 bg-surface">
          {step === 1 ? (
            <button 
              onClick={handleSkip} 
              className="text-xs text-muted hover:text-brand transition-colors sm:hidden"
            >
              Skip for now
            </button>
          ) : (
            <button onClick={back} className="btn-secondary flex items-center gap-1.5 text-xs">
              <ChevronLeft size={14} /> Back
            </button>
          )}
          {step === 1 ? (
            <button 
              onClick={next} 
              className="btn-primary flex items-center gap-1.5 text-xs ml-auto" 
              disabled={!step1Valid}
            >
              Continue <ChevronRight size={14} />
            </button>
          ) : (
            <button 
              onClick={finish} 
              className="btn-primary text-xs ml-auto" 
              disabled={!step2Valid}
            >
              Finish
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function OnboardingBanner({ onResume }) {
  return (
    <div className="border border-amber-200 bg-amber-50 px-5 py-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Building2 size={18} className="text-amber-600" />
        <div>
          <p className="text-sm font-semibold text-ink">Complete Your Onboarding</p>
          <p className="text-xs text-muted">Your profile is incomplete. Finish onboarding to unlock full platform features.</p>
        </div>
      </div>
      <button onClick={onResume} className="btn-primary text-xs whitespace-nowrap">
        Resume
      </button>
    </div>
  )
}

function Step1({ data, set }) {
  const idTypes = getCountryIdTypes(data.country)
  return (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <label className="label-above">Full Legal Name</label>
        <input
          type="text"
          value={data.fullName}
          onChange={(e) => set('fullName', e.target.value)}
          className="input-flat"
          placeholder="As it appears on your ID"
        />
      </div>
      <div>
        <label className="label-above">Operating Country</label>
        <select value={data.country} onChange={(e) => set('country', e.target.value)} className="input-flat">
          {COUNTRIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <p className="text-[11px] text-muted mt-1">ID types are tailored to your selected country.</p>
      </div>
      <div>
        <label className="label-above">Government ID Type</label>
        <select value={data.idType} onChange={(e) => set('idType', e.target.value)} className="input-flat">
          <option value="">Select ID type</option>
          {idTypes.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="label-above">Phone Number</label>
        <input
          type="tel"
          value={data.phone}
          onChange={(e) => set('phone', e.target.value)}
          className="input-flat font-mono"
          placeholder="+234 800 000 0000"
        />
      </div>
      <div>
        <label className="label-above">Home Address</label>
        <input
          type="text"
          value={data.address}
          onChange={(e) => set('address', e.target.value)}
          className="input-flat"
          placeholder="Street, city, state"
        />
      </div>
    </div>
  )
}

function Step2({ data, set, onBack }) {
  return (
    <div className="space-y-4 sm:space-y-5">
      <div>
        <label className="label-above">Registered Business Name</label>
        <input
          type="text"
          value={data.businessName}
          onChange={(e) => set('businessName', e.target.value)}
          className="input-flat"
          placeholder="Acme Innovations Ltd"
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="label-above">Operating Country</label>
          <button type="button" onClick={onBack} className="text-[11px] text-brand hover:text-brand-dark">
            Change country?
          </button>
        </div>
        <select value={data.country} onChange={(e) => set('country', e.target.value)} className="input-flat">
          {COUNTRIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="label-above">Corporate Address</label>
        <input
          type="text"
          value={data.corporateAddress}
          onChange={(e) => set('corporateAddress', e.target.value)}
          className="input-flat"
          placeholder="Registered office address"
        />
      </div>
      <div>
        <label className="label-above">Industry</label>
        <select value={data.industry} onChange={(e) => set('industry', e.target.value)} className="input-flat">
          {INDUSTRIES.map((i) => (
            <option key={i}>{i}</option>
          ))}
        </select>
      </div>
    </div>
  )
}